import { QuoteGetResponse } from '@lio/api-types';
import { useEffect, useMemo } from 'react';
import { QuoteStatusChangeItem } from '@models/quoteStatusChange';
import { PortalMessage } from '@components/portalMessage/PortalMessage';
import { PortalMessageType } from '@components/portalMessage/PortalMessageType.enum';
import Loading from '@components/Loading';
import { useSearchQuoteStatusChange } from '@services/quotes';
import { QuoteStatus } from '@models/quotes';
import { DownloadApplicationButton, yellowTextButtonStyles } from '@components/button/DownloadApplicationButton';
import { Box } from '@mui/material';

export const RejectedQuote = ({ quote }: { quote: QuoteGetResponse }) => {
  const { quoteStatusChange, fetchNextPage } = useSearchQuoteStatusChange({
    quoteId: quote.id,
    pageLimit: 50,
  });
  const allStatusChanges = quoteStatusChange?.pages.flatMap<QuoteStatusChangeItem>((page) => page.items);
  const reasons = useMemo(() => {
    if (allStatusChanges) {
      return allStatusChanges
        ?.filter((el) => el.newQuoteStatus.id === QuoteStatus.Rejected.id)
        .reduce((arr, item) => {
          if (item.quoteStatusChangeReason?.reason) {
            arr.push(item.quoteStatusChangeReason.reason);
          }
          return arr;
        }, [] as string[]);
    }
    return [];
  }, [allStatusChanges]);

  useEffect(() => {
    const totalItems = quoteStatusChange?.pages[0].pagination.total ?? 0;
    const currentTotalItems = allStatusChanges?.length ?? 0;
    if (totalItems > currentTotalItems) {
      fetchNextPage();
    }
  }, [fetchNextPage, quoteStatusChange, allStatusChanges]);

  if (!quote) return <Loading />;

  return (
    <PortalMessage
      type={PortalMessageType.Rejected}
      title={["Sorry, we can't quote this."]}
      subtitle={[
        <span key='decline-text'>
          This quote has been declined{reasons.length > 0 ? ' for the following reason(s):' : '.'}
        </span>,
      ]}
      secondarySubtitle={[
        <Box key='context-text' component='span' sx={{ display: 'flex', flexDirection: 'column' }}>
          If you have questions, please contact your LIO Underwriter.
          <span>
            You may also
            <DownloadApplicationButton
              quoteUpdatedDate={quote?.updatedAt}
              label='download your application'
              quoteId={quote.id}
              buttonProps={{
                color: 'secondary',
                variant: 'text',
                disableRipple: true,
                sx: yellowTextButtonStyles,
              }}
            />
            for your records.
          </span>
        </Box>,
      ]}
      primaryBtnText='Message Underwriter'
      primaryBtnAction={`/quote/${quote.id}?viewReadOnly=true&sidebarItem=messages`}
      secondaryBtnText='Continue to your Homepage'
      secondaryBtnAction='/'
      items={reasons}
      quote={quote}
    />
  );
};
